import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  Shield,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  ExternalLink,
  RefreshCw,
  Filter,
  Building2,
} from "lucide-react";
import { AdminLayout } from "../../components/AdminLayout";
import { getAdminScrutinyRequests } from "../../lib/adminApi";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const STATUS_CONFIG_KEYS = {
  pending: { key: "statusPending", cls: "bg-amber-500/20 text-amber-400 border-amber-500/30", icon: Clock },
  admin_approved: { key: "statusAdminApproved", cls: "bg-blue-500/20 text-blue-400 border-blue-500/30", icon: Shield },
  completed: { key: "statusCompleted", cls: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", icon: CheckCircle2 },
  expired: { key: "statusExpired", cls: "bg-red-500/20 text-red-400 border-red-500/30", icon: XCircle },
};

const formatDate = (iso) => {
  if (!iso) return "—";
  let s = String(iso).trim();
  if (!s.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(s)) s += 'Z';
  return new Date(s).toLocaleString("ca-ES", {
    timeZone: "Europe/Madrid",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getTimeRemaining = (expiresAt) => {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt) - new Date();
  if (diff <= 0) return null;
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

export default function AdminScrutinyRequestsPage() {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const res = await getAdminScrutinyRequests();
      setRequests(res.data);
    } catch {
      toast.error(t('scrutinyRequests.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const filtered =
    filter === "all" ? requests : requests.filter((r) => r.status === filter);

  const counts = {
    all: requests.length,
    pending: requests.filter((r) => r.status === "pending").length,
    admin_approved: requests.filter((r) => r.status === "admin_approved").length,
    completed: requests.filter((r) => r.status === "completed").length,
    expired: requests.filter((r) => r.status === "expired").length,
  };

  return (
    <AdminLayout>
      <div className="p-4 md:p-8" data-testid="admin-scrutiny-requests-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6"
        >
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-white">
              {t('scrutinyRequests.title')}
            </h1>
            <p className="text-slate-400 mt-1 text-sm">
              {t('scrutinyRequests.subtitle')}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadRequests}
            disabled={loading}
            className="border-slate-600 text-slate-300 hover:bg-slate-700 w-fit"
            data-testid="refresh-requests-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            {t('scrutinyRequests.refresh')}
          </Button>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex items-center gap-2 mb-6 flex-wrap"
          data-testid="scrutiny-filters"
        >
          <Filter className="h-4 w-4 text-slate-500" />
          {[
            { key: "all", labelKey: "filterAll" },
            { key: "pending", labelKey: "filterPending" },
            { key: "admin_approved", labelKey: "filterApproved" },
            { key: "completed", labelKey: "filterCompleted" },
            { key: "expired", labelKey: "filterExpired" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                filter === f.key
                  ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                  : "bg-slate-800 text-slate-400 border border-slate-700 hover:bg-slate-700"
              }`}
              data-testid={`filter-${f.key}`}
            >
              {t(`scrutinyRequests.${f.labelKey}`)} ({counts[f.key]})
            </button>
          ))}
        </motion.div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
          </div>
        ) : filtered.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-slate-800 border border-slate-700 rounded-lg p-12 text-center"
          >
            <AlertCircle className="h-12 w-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">{t('admin.noScrutinyRequests')}</p>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="space-y-3"
          >
            {filtered.map((req) => {
              const cfg = STATUS_CONFIG_KEYS[req.status] || STATUS_CONFIG_KEYS.pending;
              const Icon = cfg.icon;
              const timeLeft =
                req.status === "pending" || req.status === "admin_approved"
                  ? getTimeRemaining(req.expires_at)
                  : null;

              return (
                <div
                  key={req.id}
                  className="bg-slate-800 border border-slate-700 rounded-lg p-4 sm:p-5 hover:border-slate-600 transition-colors"
                  data-testid={`scrutiny-request-${req.id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-medium text-white truncate">
                          {req.votació_title}
                        </h3>
                        {req.request_type === 'hand_vote' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-500/20 text-orange-400 border border-orange-500/30">
                            XLSX
                          </span>
                        )}
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.cls}`}
                          data-testid={`status-badge-${req.status}`}
                        >
                          <Icon className="h-3 w-3" />
                          {t(`scrutinyRequests.${cfg.key}`)}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <Building2 className="h-3 w-3" />
                          <span className="text-slate-300">{req.entity_name}</span>
                        </span>
                        <span>
                          Email: <span className="text-slate-300">{req.entity_email}</span>
                        </span>
                        <span>
                          {t('scrutinyRequests.requested')}:{" "}
                          <span className="text-slate-300">{formatDate(req.created_at)}</span>
                        </span>
                        {req.admin_approved_at && (
                          <span>
                            {t('scrutinyRequests.approved')}:{" "}
                            <span className="text-slate-300">
                              {formatDate(req.admin_approved_at)}
                            </span>
                          </span>
                        )}
                        {req.completed_at && (
                          <span>
                            {t('scrutinyRequests.completed')}:{" "}
                            <span className="text-slate-300">
                              {formatDate(req.completed_at)}
                            </span>
                          </span>
                        )}
                        {timeLeft && (
                          <span>
                            {t('scrutinyRequests.expires')}:{" "}
                            <span className="text-amber-400">
                              {timeLeft}
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="shrink-0">
                      {req.status === "pending" && (
                        <Link
                          to={req.request_type === 'hand_vote' ? `/admin/hand-vote/${req.token}` : `/admin/scrutiny/${req.token}`}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-lg text-sm font-medium hover:bg-amber-500/30 transition-colors"
                          data-testid={`approve-link-${req.id}`}
                        >
                          <Shield className="h-4 w-4" />
                          {t('scrutinyRequests.approve')}
                          <ExternalLink className="h-3 w-3" />
                        </Link>
                      )}
                      {req.status === "admin_approved" && (
                        <span className="text-xs text-blue-400">
                          {t('scrutinyRequests.waitingEntity')}
                        </span>
                      )}
                      {req.status === "completed" && (
                        <span className="text-xs text-emerald-400">
                          {t('scrutinyRequests.finished')}
                        </span>
                      )}
                      {req.status === "expired" && (
                        <span className="text-xs text-red-400">{t('admin.expired')}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </motion.div>
        )}
      </div>
    </AdminLayout>
  );
}
